/**
 * Doctor Symptoms Review Component
 * Shows patients with unreviewed symptoms
 * Allows doctors to mark symptoms as reviewed
 */

import { useState } from 'react';
import { AlertCircle, CheckCircle2, Calendar, User, FileText, Stethoscope, Eye } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { apiClient } from '../../lib/api-client';
import { LoadingState } from '../common/LoadingState';
import { EmptyState } from '../common/EmptyState';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

interface PatientSymptomSummary {
  patient_id: number;
  patient_name: string;
  patient_age: number;
  patient_gender: string;
  patient_umr: string;
  last_reported: string;
  unreviewed_count: number;
  recent_symptoms: string[];
  patient_notes: string | null;
  symptom_logs: any[];
  severity?: string;
}

interface UnreviewedSymptomsResponse {
  patients_with_symptoms: PatientSymptomSummary[];
  total_patients: number;
  // Optional fields - may not be returned by backend yet
  total_reports?: number;
  escalated_count?: number;
  under_review_count?: number;
  resolved_count?: number;
}

// Fetch unreviewed symptoms
const fetchUnreviewedSymptoms = async (): Promise<UnreviewedSymptomsResponse> => {
  const response = await apiClient.get('/symptoms/doctor/unreviewed');
  return response;  // apiClient already returns response.data
};

// Mark symptoms as reviewed
const markSymptomsAsReviewed = async (patientId: number) => {
  const response = await apiClient.post(`/symptoms/doctor/review/${patientId}`);
  return response;  // apiClient already returns response.data
};

export function DoctorSymptomsReview() {
  const queryClient = useQueryClient();
  const [expandedPatient, setExpandedPatient] = useState<number | null>(null);

  // Fetch unreviewed symptoms
  const { data, isLoading, error } = useQuery<UnreviewedSymptomsResponse>({
    queryKey: ['unreviewedSymptoms'],
    queryFn: fetchUnreviewedSymptoms,
    refetchInterval: 60000, // Refetch every minute
  });

  // ✅ DEBUG: Log the response
  console.log('🔍 DoctorSymptomsReview - API Response:', {
    data,
    isLoading,
    error,
    patientsCount: data?.patients_with_symptoms?.length || 0
  });

  // Mark as reviewed mutation
  const markAsReviewedMutation = useMutation({
    mutationFn: markSymptomsAsReviewed,
    onSuccess: (response, patientId) => {
      toast.success('Symptoms Reviewed', {
        description: `${response.reviewed_count || 'All'} symptom(s) marked as reviewed`,
      });
      
      // Invalidate and refetch to update the list
      queryClient.invalidateQueries({ queryKey: ['unreviewedSymptoms'] });
      
      // Close expanded view if it was the reviewed patient
      if (expandedPatient === patientId) {
        setExpandedPatient(null);
      }
    },
    onError: (error: any) => {
      toast.error('Failed to mark symptoms as reviewed', {
        description: error?.response?.data?.detail || error.message,
      });
    },
  });

  const handleMarkAsReviewed = (patientId: number) => {
    markAsReviewedMutation.mutate(patientId);
  };

  const toggleExpand = (patientId: number) => {
    setExpandedPatient(expandedPatient === patientId ? null : patientId);
  };

  // Get severity color
  const getSeverityColor = (severity?: string) => {
    switch (severity?.toLowerCase()) {
      case 'severe':
        return 'bg-red-100 text-red-600 border-red-200';
      case 'significant':
      case 'moderate':
        return 'bg-[#FEEBDA] text-[#F07D13] border-[#F07D13]/20';
      case 'mild':
        return 'bg-blue-100 text-[#14CAF4] border-blue-200';
      default:
        return 'bg-[#FEEBDA] text-[#F07D13] border-[#F07D13]/20';
    }
  };

  if (isLoading) {
    return <LoadingState message="Loading patient symptoms..." />;
  }

  if (error) {
    return (
      <Card className="shadow-lg">
        <CardContent className="p-6">
          <EmptyState
            icon={AlertCircle}
            title="Error loading symptoms"
            description="Failed to load patient symptoms. Please try again."
          />
        </CardContent>
      </Card>
    );
  }

  const patientsWithSymptoms = data?.patients_with_symptoms || [];
  const totalReports = data?.total_reports || patientsWithSymptoms.length;
  const escalated = data?.escalated_count || 0;
  const underReview = data?.under_review_count || patientsWithSymptoms.length;
  const resolved = data?.resolved_count || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-primary text-2xl">Patient Symptoms Tracker</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Monitor and manage patient symptom reports
        </p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground mb-1">Total Reports</p>
            <p className="text-3xl text-primary">{totalReports}</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-red-500">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground mb-1">Escalated</p>
            <p className="text-3xl text-red-500">{escalated}</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-[#14CAF4]">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground mb-1">Under Review</p>
            <p className="text-3xl text-[#14CAF4]">{underReview}</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground mb-1">Resolved</p>
            <p className="text-3xl text-green-500">{resolved}</p>
          </CardContent>
        </Card>
      </div>

      {/* Patients List */}
      {patientsWithSymptoms.length === 0 ? (
        <Card className="shadow-lg">
          <CardContent className="p-6">
            <EmptyState
              icon={CheckCircle2}
              title="All Caught Up!"
              description="No unreviewed symptoms at the moment. All patient symptoms have been reviewed."
            />
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {patientsWithSymptoms.map((patient) => (
            <Card 
              key={patient.patient_id} 
              className="shadow-lg hover:shadow-xl transition-shadow"
            >
              <CardContent className="p-6">
                {/* Patient Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#2762AC] flex items-center justify-center flex-shrink-0">
                      <span className="text-white">
                        {patient.patient_name
                          ? patient.patient_name
                              .split(' ')
                              .map(n => n[0])
                              .join('')
                              .toUpperCase()
                              .slice(0, 2)  // Limit to 2 initials
                          : 'UN'}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-primary">{patient.patient_name || 'Unknown'}</h3>
                      <p className="text-sm text-muted-foreground">
                        Hospital not specified
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {(() => {
                          try {
                            const reportDate = new Date(patient.last_reported);
                            return format(reportDate, 'MMM d, yyyy');
                          } catch {
                            return patient.last_reported;
                          }
                        })()}
                      </p>
                    </div>
                  </div>
                  <Badge 
                    variant="outline" 
                    className={getSeverityColor(patient.severity || 'moderate')}
                  >
                    {patient.severity ? patient.severity.charAt(0).toUpperCase() + patient.severity.slice(1) : 'Significant'}
                  </Badge>
                </div>

                {/* Reported Symptoms */}
                <div className="mb-4">
                  <p className="text-sm mb-2">Reported Symptoms:</p>
                  <ul className="list-disc list-inside space-y-1">
                    {patient.recent_symptoms.map((symptom, index) => (
                      <li key={index} className="text-sm text-foreground">
                        {symptom}
                      </li>
                    ))}
                    {patient.recent_symptoms.length === 0 && (
                      <li className="text-sm text-muted-foreground">No specific symptoms noted</li>
                    )}
                  </ul>
                </div>

                {/* Patient Notes */}
                {patient.patient_notes && (
                  <div className="bg-[#FEEBDA] border border-[#F07D13]/20 rounded-lg p-3 mb-4">
                    <div className="flex items-start gap-2">
                      <FileText className="w-4 h-4 text-[#F07D13] mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-[#F07D13] mb-1">Additional Notes:</p>
                        <p className="text-sm text-foreground">{patient.patient_notes}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => toggleExpand(patient.patient_id)}
                    className="border-[#2762AC] text-[#2762AC] hover:bg-[#2762AC] hover:text-white"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    {expandedPatient === patient.patient_id ? 'Hide Details' : 'View Details'}
                  </Button>
                  
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleMarkAsReviewed(patient.patient_id)}
                    disabled={markAsReviewedMutation.isPending}
                    className="border-[#14CAF4] text-[#14CAF4] hover:bg-[#14CAF4] hover:text-white"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    {markAsReviewedMutation.isPending ? 'Reviewing...' : 'Review'}
                  </Button>
                </div>

                {/* Expanded Full History */}
                {expandedPatient === patient.patient_id && (
                  <div className="mt-6 pt-6 border-t space-y-4">
                    <h4 className="font-medium text-primary mb-4">Complete Symptom History</h4>
                    {patient.symptom_logs.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No symptom history available</p>
                    ) : (
                      patient.symptom_logs.map((log) => {
                        const logDate = new Date(log.recorded_at);
                        const symptoms = log.symptoms || {};
                        
                        return (
                          <Card key={log.id} className="border-l-4 border-l-[#14CAF4]">
                            <CardContent className="p-4">
                              <div className="flex items-start justify-between mb-3">
                                <div>
                                  <p className="text-foreground font-medium">
                                    {format(logDate, 'PPPP')}
                                  </p>
                                  <p className="text-muted-foreground text-sm">
                                    Logged at {format(logDate, 'p')}
                                  </p>
                                </div>
                                <Badge variant="outline" className={
                                  log.severity === 'severe' ? 'bg-red-50 border-red-200 text-red-700' :
                                  log.severity === 'significant' ? 'bg-orange-50 border-orange-200 text-orange-700' :
                                  'bg-green-50 border-green-200 text-green-700'
                                }>
                                  {log.severity || 'Mild'}
                                </Badge>
                              </div>

                              {/* Symptom Metrics Grid */}
                              {Object.keys(symptoms).length > 0 && (
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
                                  {Object.entries(symptoms).map(([key, value]: [string, any]) => {
                                    const numValue = typeof value === 'number' ? value : 0;
                                    return (
                                      <div key={key} className="p-2 bg-secondary rounded-lg">
                                        <p className="text-xs text-muted-foreground mb-1 capitalize">
                                          {key.replace(/_/g, ' ')}
                                        </p>
                                        <div className="flex items-center gap-2">
                                          <div className="flex-1 bg-white rounded-full h-2">
                                            <div 
                                              className={`h-2 rounded-full transition-all ${
                                                key === 'energy_level' || key === 'sleep_quality' 
                                                  ? 'bg-[#14CAF4]' 
                                                  : 'bg-primary'
                                              }`}
                                              style={{ width: `${numValue * 10}%` }}
                                            />
                                          </div>
                                          <span className="text-sm text-foreground">{numValue}/10</span>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}

                              {/* Triggers */}
                              {log.triggers && log.triggers.length > 0 && (
                                <div className="mb-3">
                                  <p className="text-xs text-muted-foreground mb-2">Triggers:</p>
                                  <div className="flex flex-wrap gap-2">
                                    {log.triggers.map((trigger: string, idx: number) => (
                                      <Badge 
                                        key={idx} 
                                        variant="outline"
                                        className="bg-amber-50 text-amber-700 border-amber-200"
                                      >
                                        {trigger}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Additional Notes */}
                              {log.additional_notes && (
                                <div className="p-3 bg-muted rounded-lg">
                                  <p className="text-xs text-muted-foreground mb-1">Additional Notes:</p>
                                  <p className="text-sm text-foreground">{log.additional_notes}</p>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        );
                      })
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}